import React, { useState, useEffect } from 'react';
import { Excalidraw } from '@excalidraw/excalidraw';
import { auth } from '../firebase';
import { uploadDataUrl, urlToDataUrl } from '../services/imageUploadService';
import './WhiteboardEditor.css';

function WhiteboardEditor({ diagram, onClose, onSave }) {
  const [excalidrawAPI, setExcalidrawAPI] = useState(null);
  const [saving, setSaving] = useState(false);

  // Parse JSON strings if they exist, otherwise use arrays
  const initialData = {
    elements: diagram.content?.elementsJSON
      ? JSON.parse(diagram.content.elementsJSON)
      : (diagram.content?.elements || []),
    appState: diagram.content?.appStateJSON
      ? JSON.parse(diagram.content.appStateJSON)
      : (diagram.content?.appState || {}),
    scrollToContent: true
  };

  // Build 28 (item 2): rehydrate images. We persist a fileMap of
  // { fileId: { url, mimeType } } pointing at Storage (NOT base64, to
  // stay under the Firestore 1MB doc limit). On load we fetch each URL
  // back to a dataURL and feed Excalidraw via addFiles().
  useEffect(() => {
    if (!excalidrawAPI) return;
    let cancelled = false;
    (async () => {
      try {
        const fileMap = diagram.content?.fileMapJSON
          ? JSON.parse(diagram.content.fileMapJSON)
          : null;
        if (!fileMap || Object.keys(fileMap).length === 0) {
          console.log('[WhiteboardEditor] no fileMap to rehydrate');
          return;
        }
        console.log('[WhiteboardEditor] rehydrating', Object.keys(fileMap).length, 'image(s)');
        const files = [];
        for (const [fileId, meta] of Object.entries(fileMap)) {
          if (!meta || !meta.url) {
            console.warn('[WhiteboardEditor] skipping fileId with no url:', fileId);
            continue;
          }
          try {
            const dataURL = await urlToDataUrl(meta.url);
            files.push({
              id: fileId,
              dataURL,
              mimeType: meta.mimeType || 'image/png',
              created: Date.now()
            });
          } catch (perFileErr) {
            console.error('[WhiteboardEditor] failed to load image for', fileId, perFileErr);
          }
        }
        if (cancelled) return;
        if (files.length === 0) {
          console.warn('[WhiteboardEditor] no images loaded successfully');
          return;
        }
        if (excalidrawAPI.addFiles) {
          console.log('[WhiteboardEditor] addFiles with', files.length, 'file(s)');
          excalidrawAPI.addFiles(files);
        }
      } catch (e) {
        console.error('[WhiteboardEditor] rehydrate top-level error:', e);
      }
    })();
    return () => { cancelled = true; };
  }, [excalidrawAPI, diagram]);

  const handleSave = async () => {
    if (!excalidrawAPI || saving) return;
    setSaving(true);
    try {
      const elements = excalidrawAPI.getSceneElements();
      const appState = excalidrawAPI.getAppState();
      const files = excalidrawAPI.getFiles ? excalidrawAPI.getFiles() : {};

      // Build 28 (item 2): offload each image binary to Storage and
      // keep only its URL. The key DRY win — reuses the shared
      // imageUploadService instead of base64-embedding (Build 24's 1MB
      // problem). Only image elements still on the canvas are kept.
      const usedFileIds = new Set(
        (elements || [])
          .filter(el => el.type === 'image' && el.fileId && !el.isDeleted)
          .map(el => el.fileId)
      );

      const prevMap = diagram.content?.fileMapJSON
        ? JSON.parse(diagram.content.fileMapJSON)
        : {};
      const fileMap = {};

      for (const fileId of usedFileIds) {
        // Already offloaded in a previous save? Reuse the URL.
        if (prevMap[fileId] && prevMap[fileId].url) {
          fileMap[fileId] = prevMap[fileId];
          continue;
        }
        const f = files[fileId];
        if (!f || !f.dataURL) continue;
        const url = await uploadDataUrl(f.dataURL, {
          scope: 'diagrams',
          ownerId: (auth.currentUser && auth.currentUser.uid) || 'anonymous',
          name: `wb_${fileId}`
        });
        fileMap[fileId] = { url, mimeType: f.mimeType || 'image/png' };
      }

      const content = {
        elementsJSON: JSON.stringify(elements || []),
        appStateJSON: JSON.stringify({
          viewBackgroundColor: appState.viewBackgroundColor || '#ffffff',
          gridSize: appState.gridSize || null
        }),
        // URLs only — no base64 — so the doc stays small.
        fileMapJSON: JSON.stringify(fileMap)
      };

      await onSave(content);
      alert('Diagram saved!');
    } catch (err) {
      console.error('Whiteboard save error:', err);
      alert(`Error saving diagram: ${err?.message || 'unknown error'}`);
    } finally {
      setSaving(false);
    }
  };

  const handleExport = async (format) => {
    if (!excalidrawAPI) return;
    try {
      if (format === 'PNG') {
        const blob = await excalidrawAPI.exportToBlob({
          mimeType: 'image/png',
          quality: 0.95
        });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `${diagram.title}.png`;
        a.click();
        URL.revokeObjectURL(url);
      } else if (format === 'SVG') {
        const svg = await excalidrawAPI.exportToSvg();
        const svgData = new XMLSerializer().serializeToString(svg);
        const blob = new Blob([svgData], { type: 'image/svg+xml' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `${diagram.title}.svg`;
        a.click();
        URL.revokeObjectURL(url);
      }
    } catch (err) {
      console.error('Export error:', err);
      alert('Error exporting diagram');
    }
  };

  return (
    <div className="whiteboard-editor">
      <header className="editor-header">
        <div className="editor-title">
          <h2>✏️ {diagram.title}</h2>
          <span className="editor-mode-badge mode-whiteboard">Whiteboard Mode</span>
        </div>
        <div className="editor-actions">
          <button className="btn btn-secondary btn-small" onClick={handleSave} disabled={saving}>
            {saving ? '⏳ Saving…' : '💾 Save'}
          </button>
          <button className="btn btn-secondary btn-small" onClick={() => handleExport('PNG')}>
            📥 PNG
          </button>
          <button className="btn btn-secondary btn-small" onClick={() => handleExport('SVG')}>
            📥 SVG
          </button>
          <button className="btn btn-secondary btn-small" onClick={onClose}>
            ✕ Close
          </button>
        </div>
      </header>

      <div className="whiteboard-canvas">
        <Excalidraw
          excalidrawAPI={(api) => setExcalidrawAPI(api)}
          initialData={initialData}
          theme="light"
        />
      </div>
    </div>
  );
}

export default WhiteboardEditor;
